"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, FileText, PackageCheck, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const principles = [{
  number: "01",
  icon: PackageCheck,
  title: "Straightforward products.",
  description: "A focused catalog with clear product names, strengths, and research-use identification."
}, {
  number: "02",
  icon: FileText,
  title: "Accessible documentation.",
  description: "Applicable product and testing documentation is available upon request from our team."
}, {
  number: "03",
  icon: ShieldCheck,
  title: "Research focused.",
  description: "Our products and website information are presented specifically for research-use purposes."
}];
export default function Disclaimer() {
  const shouldReduceMotion = useReducedMotion();
  const motionProps = (delay = 0) => ({
    initial: shouldReduceMotion ? false : {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.18
    },
    transition: {
      duration: shouldReduceMotion ? 0 : 0.65,
      delay: shouldReduceMotion ? 0 : delay,
      ease: [0.16, 1, 0.3, 1] as const
    }
  });
  return <section id="research-use-disclaimer" data-nav="light" aria-labelledby="research-use-disclaimer-heading" className="overflow-hidden bg-[#f8fafb] px-4 pb-8 pt-6 text-[#0a1218] sm:px-6 md:pt-10 lg:px-8">
      <div className="mx-auto max-w-[1440px]">

        {/* -------------------------------------------------------------- */}
        {/* BRAND STATEMENT                                                */}
        {/* -------------------------------------------------------------- */}

        <motion.div {...motionProps()} className="border-t border-[#dbe6eb] pb-20 pt-20 md:pb-28 md:pt-28 lg:pb-36 lg:pt-36">
          <div className="mb-8 flex items-center gap-3">
            <span aria-hidden="true" className="h-px w-8 bg-[#159fdf]" />

            <span className="text-[10px] font-semibold uppercase tracking-[0.23em] text-[#1479a7]">
              TEK TIDES LABS
            </span>
          </div>

          <div className="grid gap-12 lg:grid-cols-[1.25fr_0.75fr] lg:items-end">
            <h2 id="research-use-disclaimer-heading" className="max-w-[900px] text-[clamp(3.2rem,6.4vw,7.4rem)] font-semibold leading-[0.9] tracking-[-0.07em] text-[#0a1218]">
              Research moves
              <br />
              forward.
              <span className="text-[#159fdf]"> So do we.</span>
            </h2>

            <div className="max-w-[410px] lg:justify-self-end lg:pb-2">
              <p className="text-[15px] leading-7 text-[#56636a]">
                TEK TIDES LABS is built around a straightforward idea:
                keep the research ordering experience clear, organized,
                and easy to navigate.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-5">
                <Button asChild className="group h-11 rounded-full bg-[#087db3] px-6 text-sm font-semibold text-white shadow-none hover:bg-[#066d9d]">
                  <Link to="/ShopPage">
                    Explore Products

                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </Button>

                <Link to="/AboutPage" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#087db3] transition-colors hover:text-[#055f8a]">
                  About TEK TIDES

                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* -------------------------------------------------------------- */}
        {/* PRINCIPLES                                                     */}
        {/* -------------------------------------------------------------- */}

        <div className="grid border-y border-[#dbe6eb] md:grid-cols-3">
          {principles.map((item, index) => {
          const Icon = item.icon;
          return <motion.article key={item.number} {...motionProps(index * 0.08)} className={["group py-10 md:min-h-[300px] md:py-12", index === 0 ? "md:pr-10" : "", index === 1 ? "border-y border-[#dbe6eb] md:border-x md:border-y-0 md:px-10" : "", index === 2 ? "md:pl-10" : ""].join(" ")}>
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-medium tracking-[0.16em] text-[#87969d]">
                    {item.number}
                  </span>

                  <div className="flex size-10 items-center justify-center rounded-full border border-[#dbe6eb] text-[#087db3] transition-colors duration-300 group-hover:border-[#159fdf]/40 group-hover:bg-[#eef5f8]">
                    <Icon className="size-[18px]" strokeWidth={1.4} aria-hidden="true" />
                  </div>
                </div>

                <div className="mt-16">
                  <h3 className="max-w-[300px] text-[25px] font-semibold leading-[1.05] tracking-[-0.04em] text-[#0a1218]">
                    {item.title}
                  </h3>

                  <p className="mt-5 max-w-[330px] text-sm leading-6 text-[#56636a]">
                    {item.description}
                  </p>
                </div>
              </motion.article>;
        })}
        </div>

        {/* -------------------------------------------------------------- */}
        {/* FINAL CTA                                                      */}
        {/* -------------------------------------------------------------- */}

        <motion.div {...motionProps(0.1)} className="py-8 md:py-12">
          <div className="relative overflow-hidden rounded-[34px] bg-[#eef5f8] px-7 py-16 sm:px-12 md:py-20 lg:px-20 lg:py-24">

            <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-32 size-[420px] rounded-full bg-[#159fdf]/10 blur-[100px]" />

            <div className="relative z-10 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.23em] text-[#1479a7]">
                  Explore TEK TIDES LABS
                </p>

                <h3 className="mt-6 max-w-[760px] text-[clamp(2.8rem,5vw,5.8rem)] font-semibold leading-[0.93] tracking-[-0.06em] text-[#0a1218]">
                  Find what your
                  <br />
                  research needs next.
                </h3>
              </div>

              <Button asChild className="group h-12 shrink-0 rounded-full bg-[#087db3] px-7 text-sm font-semibold text-white shadow-none hover:bg-[#066d9d]">
                <Link to="/ShopPage">
                  View Catalog

                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>

        {/* -------------------------------------------------------------- */}
        {/* RESEARCH USE NOTICE                                            */}
        {/* -------------------------------------------------------------- */}

        <motion.div {...motionProps(0.1)} className="border-t border-[#dbe6eb] py-8">
          <div className="grid gap-5 lg:grid-cols-[180px_1fr]">
            <div>
              <div className="inline-flex items-center gap-2">
                <ShieldCheck className="size-4 text-[#087db3]" strokeWidth={1.5} aria-hidden="true" />

                <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0a1218]">
                  Research Use Only
                </h3>
              </div>
            </div>

            <div className="max-w-[980px] space-y-3 text-[11px] leading-5 text-[#718087]">
              <p>
                Products offered by TEK TIDES LABS are supplied strictly
                for research use only and are not intended for diagnostic,
                clinical, therapeutic, human, or veterinary applications.
              </p>

              <p>
                Information provided on this website is for product
                identification and research-reference purposes only and
                should not be interpreted as medical or veterinary advice,
                dosage or administration guidance, treatment
                recommendations, or claims of therapeutic benefit.
              </p>

              <p>
                Statements and information presented on this website have
                not been evaluated by the U.S. Food and Drug Administration.
                Products offered by TEK TIDES LABS are not intended to
                diagnose, treat, cure, mitigate, or prevent any disease.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}